/**
 * Разбор ошибок API до маршрута.
 *
 * Глобального редиректа на 403 в интерцепторе намеренно нет: один отказ в
 * праве не должен уводить с экрана, где остальные запросы прошли — иначе
 * виджет без права выкидывал бы пользователя с рабочей доски. Решает тот, кто
 * грузит экран: ответ на GET самой сущности — повод показать 403 или 404,
 * ответ фонового запроса — нет.
 */
import axios from 'axios'
import type { RouteLocationRaw } from 'vue-router'

const statusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined

export const isForbidden = (error: unknown): boolean => statusOf(error) === 403

export const isNotFound = (error: unknown): boolean => statusOf(error) === 404

/**
 * Что показать человеку. Бэкенд отвечает grpc-gateway-ошибкой `{message}` —
 * она осмысленная («project key already exists»), и глотать её в пользу
 * общего «что-то пошло не так» значит прятать причину. Запасной текст нужен
 * для сетевых отказов, где сообщения нет вовсе.
 */
export const errorMessage = (error: unknown, fallback: string): string => {
  if (!axios.isAxiosError(error)) return fallback

  const message = (error.response?.data as { message?: string } | undefined)?.message

  return message || fallback
}

/**
 * Маршрут ошибки для отказа при загрузке экрана, либо null — тогда ошибку
 * показывает сам экран.
 */
/**
 * Куда уводить с экрана, который отказали показать.
 *
 * Ключ проекта передаётся в адрес: 403 — единственное место, откуда просят
 * доступ, а больше о проекте у экрана ничего нет (DEV-1493).
 */
export const errorRoute = (error: unknown, projectKey = ''): RouteLocationRaw | null => {
  if (isForbidden(error)) {
    return { name: 'forbidden', query: projectKey ? { key: projectKey } : {} }
  }

  if (isNotFound(error)) return { name: 'not-found' }

  return null
}
